import { useCallback, useEffect, useRef, useState } from "react";
import { Subject } from "rxjs";
import { getUniqueId } from "@jugl-web/utils";
import isEqual from "lodash/isEqual";
import type { TableCustomer } from "../types";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import type { FormField } from "../../customer-form-field/types";

export const customersListEvents$ = new Subject<
  | { type: "add"; customer: TableCustomer }
  | { type: "remove"; customerId: string }
  | { type: "update"; customer: TableCustomer }
>();

export type UseCustomersListParams = UsePaginatedRequestHookParams<{
  entityId: string;
  searchTerm?: string;
  searchBy?: { id: string; values: FormField["values"] }[];
  sortBy?: {
    id: string;
    type: FormField["type"];
  };
  sortOrder?: "asc" | "desc";
  view?: "table";
}>;

export type UseCustomersListReturn =
  UsePaginatedRequestHookReturn<TableCustomer> & { totalEntries: number };

export const useCustomersList = ({
  entityId,
  searchTerm = "",
  searchBy,
  sortBy,
  sortOrder,
  pageSize = 20,
  view,
  skip,
}: UseCustomersListParams): UseCustomersListReturn => {
  const { customersApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [customers, setCustomers] = useState<TableCustomer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);

  const [fetchCustomers] = customersApi.useLazyTableCustomersListQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchCustomers({
          entityId,
          params: {
            page,
            page_size: pageSize,
            search_term: searchTerm,
            search_by: searchBy,
            sort_by: sortBy,
            sort_order: sortOrder,
            view,
          },
        }).unwrap();
        setTotalEntries(result.total_entries);

        if (requestId.current !== newRequestId) return;

        if (result) {
          setCustomers((prev) => {
            // If page 1, replace all
            if (page === 1) {
              return result.data;
            }

            // Otherwise append new customers
            const existingIds = new Set(prev.map((c) => c.id));
            const newCustomers = result.data.filter(
              (c) => !existingIds.has(c.id)
            );

            return [...prev, ...newCustomers];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      fetchCustomers,
      entityId,
      pageSize,
      searchTerm,
      searchBy,
      sortBy,
      sortOrder,
      view,
    ]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setCustomers([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && customers.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, customers.length, loadPage]);

  // Load initial page when component mounts or parameters change
  const previousSearchBy = useRef<
    { id: string; values: FormField["values"] }[] | undefined
  >(searchBy || undefined);
  const previousSortOrder = useRef<"asc" | "desc" | undefined>(
    sortOrder || undefined
  );
  const previousSortBy = useRef<
    | {
        id: string;
        type: FormField["type"];
      }
    | undefined
  >(sortBy || undefined);
  const previousSearchTerm = useRef<string>(searchTerm || "");
  const isInitialized = useRef(false);
  useEffect(() => {
    if (skip) {
      isInitialized.current = false;
      return;
    }
    const load = () => {
      setCustomers([]);
      setCurrentPage(1);
      setTotalPages(null);
      setIsError(false);
      loadPage(1);
    };
    if (!isInitialized.current) {
      load();
      isInitialized.current = true;
      return;
    }
    if (
      (previousSearchTerm.current === searchTerm ||
        (!previousSearchTerm.current && !searchTerm)) &&
      (isEqual(previousSearchBy.current, searchBy) ||
        (!isEqual(previousSearchBy.current, searchBy) && !searchTerm)) &&
      isEqual(previousSortOrder.current, sortOrder) &&
      isEqual(previousSortBy.current, sortBy)
    ) {
      return;
    }
    previousSearchTerm.current = searchTerm;
    previousSearchBy.current = searchBy;
    previousSortOrder.current = sortOrder;
    previousSortBy.current = sortBy;
    load();
  }, [searchTerm, searchBy, sortBy, sortOrder, loadPage, skip]);

  // Listen to Subject for real-time customer updates
  useEffect(() => {
    const subscription = customersListEvents$.subscribe((action) => {
      if (action.type === "add") {
        const { customer } = action;
        setCustomers((prev) => {
          const wasNewInList = !prev.some((c) => c.id === customer.id);
          // Remove customer if it already exists
          const filtered = prev.filter((c) => c.id !== customer.id);
          if (wasNewInList) {
            setTotalEntries((total) => total + 1);
          }

          // Prepend customer to the top
          return [customer, ...filtered];
        });
      } else if (action.type === "remove") {
        const { customerId } = action;
        setCustomers((prev) => {
          const wasInList = prev.some((c) => c.id === customerId);
          if (wasInList) {
            setTotalEntries((total) => Math.max(0, total - 1));
          }
          return prev.filter((c) => c.id !== customerId);
        });
      } else if (action.type === "update") {
        const { customer } = action;
        setCustomers((prev) => {
          const existingIndex = prev.findIndex((c) => c.id === customer.id);

          if (existingIndex >= 0) {
            // Update existing customer in place
            const newList = [...prev];
            newList[existingIndex] = customer;
            return newList;
          }

          // If not found, don't add it
          return prev;
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && currentPage > 1;
  const isLoadingMoreError =
    isError && (currentPage > 1 || customers.length === 0);

  return {
    data: customers,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
    totalEntries,
  };
};
